import type { ReactNode } from 'react';
import { Card } from './card';

/**
 * A titled region of a screen: the heading, whatever control belongs beside it, and the content.
 *
 * **The heading level is a parameter because the portal has two page shapes.** On the dashboard
 * these sit under one `h1` and are `h2`s; on a case page some of them are nested a level deeper.
 * Getting that wrong produces a document whose outline a screen-reader user cannot navigate by,
 * which is the one thing a heading is for.
 *
 * `icon` is beside the title rather than above it, so a customer scanning the page finds
 * "documents" by shape before reading a word — which is how somebody uses this screen when they
 * already know what they came for.
 */
export function Panel({
    title,
    icon,
    action,
    level = 2,
    padded = true,
    children
}: {
    title: string;
    icon?: ReactNode;
    /** A button or link on the heading row. Never the panel's primary action. */
    action?: ReactNode;
    level?: 2 | 3;
    /** False when the content draws its own edges — a list of rows that should meet the border. */
    padded?: boolean;
    children: ReactNode;
}) {
    const Heading = level === 2 ? 'h2' : 'h3';

    return (
        <Card as="section">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 px-4 py-3.5 sm:px-5 sm:py-4 dark:border-slate-800">
                <Heading className="flex items-center gap-2.5 text-base font-bold text-slate-900 dark:text-slate-100">
                    {icon !== undefined && (
                        <span className="text-brand-600 dark:text-brand-400">{icon}</span>
                    )}
                    {title}
                </Heading>

                {action}
            </div>

            <div className={padded ? 'p-4 sm:p-5' : ''}>{children}</div>
        </Card>
    );
}
